import React, { useEffect, useState } from 'react';
import { FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import moment from 'moment';
import { useTheme } from '@mui/material/styles';
import LoadingButton from '@mui/lab/LoadingButton';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MainCard from 'components/Cards/MainCard';
import SubCard from 'components/Cards/SubCard';
import ReportFileExports from 'components/Reports/FileExports';
import ApiHelper from 'utils/apiHelper';
import { gridSpacing, API_URL } from 'utils/constants';
import { handleUpdateReportDateRange, convertDate, validateCustomDateRange } from 'utils/commonHelpers';
import ReportVisualization from 'components/Reports/ReportVisualization';

const initialState = {
    User_Active: [],
    All_On: [],
    CPU_On: [],
    All_Asleep: [],
    All_Off: [],
    Unknown: [],
    dates: []
};

const ReportsPowerState = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const currentDate = new Date();
    const today = new Date();
    const [startDate, setStartDate] = useState(currentDate.setMonth(currentDate.getMonth() - 1));
    const [endDate, setEndDate] = useState(today.setDate(today.getDate() - 1));
    const [dateRangeValue, setDateRangeValue] = useState('1month');
    const [timeInterval, setTimeInterval] = useState('day');
    const [groupId, setGroupId] = useState(0);
    const [policyId, setPolicyId] = useState(0);
    const [groupsData, setGroupsData] = useState([]);
    const [policiesData, setPoliciesData] = useState([]);
    const [powerStatesData, setPowerStatesData] = useState(initialState);
    const [viewData, setViewData] = useState([]);
    const [viewId, setViewId] = useState(0);
    const [viewStatements, setViewStatements] = useState([]);
    const [filters, setFilters] = useState({});
    const [timeIntervalDateRanges, setTimeIntervalDateRanges] = useState({});

    const initialViewData = async (selectedItem) => {
        setLoading(true);
        try {
            // Get views list and statements
            const [views, viewStatements] = await Promise.all([
                ApiHelper.get('/api/management/views/'),
                ApiHelper.get('/api/management/get_view_statements/')
            ]);

            // Only proceed if we have views
            if (views.data.res.length > 0) {
                // Update views list and statements
                setViewData(views.data.res);
                setViewStatements(viewStatements.data);
            }
        } catch (err) {
            if (err.response?.status !== 401) {
                toast.error('Failed to fetch views data');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getTimeIntervalDateRanges();
        getGroups();
        getPolicies();
        initialViewData();
    }, []);

    const getTimeIntervalDateRanges = () => {
        ApiHelper.get('/api/settings/constants/', {}, {}, { values: 'time_interval_date_ranges' }).then((res) => {
            setTimeIntervalDateRanges(res.data['timeIntervalDateRanges']);
        });
    };

    const getGroups = () => {
        ApiHelper.get('/api/reports/groups/')
            .then((res) => {
                setGroupsData(res.data);
            })
            .catch();
    };

    const getPolicies = () => {
        ApiHelper.get(API_URL.MANAGEMENT_POLICY)
            .then((res) => {
                setPoliciesData(res.data.policy_data);
            })
            .catch();
    };

    const handleChangeDateRangeValue = (e) => {
        const value = e.target.value;
        setDateRangeValue(value);
        const updatedStartDate = handleUpdateReportDateRange(value);
        setStartDate(updatedStartDate);
    };

    const getPowerStateData = () => {
        if (convertDate(startDate) === 'Invalid date') {
            toast.error('Fail! The start date format is incorrect');
            return true;
        }
        if (convertDate(endDate) === 'Invalid date') {
            toast.error('Fail! The end date format is incorrect');
            return true;
        }
        if (moment(convertDate(startDate)) > moment(convertDate(endDate))) {
            toast.error('Fail! The start date can not be greater than the end date');
            return true;
        }

        if (dateRangeValue === '0') {
            const validation = validateCustomDateRange(startDate, endDate, timeInterval);
            if (!validation.isValid) {
                toast.error(`Fail! ${validation.message}`);
                return true;
            }
        }
        setLoading(true);
        // Get selected view statements
        const selectedStatements = viewStatements.filter((statement) => statement.view_id === viewId - 1);

        // Format statements for API call
        const statement = selectedStatements.map((statement, index) => ({
            id: index + 1,
            attribute: statement.deviceattrib,
            negative: statement.negative === 1,
            operator: statement.operator_id,
            value: statement.value,
            connector: index === selectedStatements.length - 1 ? '1' : statement.connector_id
            // operatorOptions: commonData.operators
        }));
        ApiHelper.post('/api/reports/power_state/', {
            start_date: convertDate(startDate),
            end_date: convertDate(endDate),
            time_interval: timeInterval,
            group_id: groupId === 0 ? '' : groupId.toString(),
            policy_id: policyId === 0 ? '' : policyId.toString(),
            statement: statement.length > 0 ? statement : []
        })
            .then((res) => {
                console.log(res.data);
                setPowerStatesData(res.data);
                setLoading(false);
                toast.success('Successfully done');
                let groupName = 'Any';
                let policyName = 'Any';
                let viewName = 'Any';
                for (let i = 0; i < groupsData.length; i++) {
                    if (groupsData[i].id === groupId) {
                        groupName = groupsData[i].name;
                    }
                }
                for (let i = 0; i < policiesData.length; i++) {
                    if (policiesData[i].policyid === policyId) {
                        policyName = policiesData[i].name;
                    }
                }
                if (viewId !== 0) {
                    for (let i = 0; i < viewData.length; i++) {
                        if (viewData[i].id === viewId - 1) {
                            viewName = viewData[i].name;
                        }
                    }
                }

                const filterData = [
                    ['Start Date', convertDate(startDate)],
                    ['End Date', convertDate(endDate)],
                    ['Time Interval', timeInterval],
                    ['Group Name', groupName],
                    ['Policy Name', policyName],
                    ['View Name', viewName]
                ];
                setFilters({
                    start_date: convertDate(startDate),
                    end_date: convertDate(endDate),
                    time_interval: timeInterval,
                    group_id: groupId === 0 ? '' : groupId.toString(),
                    policy_id: policyId === 0 ? '' : policyId.toString(),
                    statement: statement.length > 0 ? statement : [],
                    report: 'Power states vs Time',
                    filterData: filterData
                });
            })
            .catch(() => {
                setPowerStatesData(initialState);
                setLoading(false);
                toast.warning('There is no power state data');
            });
    };

    const handleTimeIntervalChange = (e) => {
        const newInterval = e.target.value;
        const availableRanges = timeIntervalDateRanges[newInterval];
        setTimeInterval(newInterval);
        setDateRangeValue(availableRanges[0].value);
        const updatedStartDate = handleUpdateReportDateRange(availableRanges[0].value);
        setStartDate(updatedStartDate);
    };

    const getHelperText = (timeInterval) => {
        switch (timeInterval) {
            case 'hour':
                return '2 weeks';
            case 'day':
                return '3 months';
            case 'week':
                return '6 months';
            case 'month':
                return '1 year';
            default:
                return '';
        }
    };

    const options = {
        colors: ['#ffffff', '#036705', '#03F948', '#F4FB04', '#FB041F', '#045DFB'],
        chart: {
            type: 'column',
            height: 600
        },
        title: {
            text: null
        },
        credits: {
            enabled: false
        },
        legend: {
            symbolHeight: 12, // Adjust height if needed
            symbolWidth: 12, // Adjust width if needed
            symbolRadius: 0,
            reversed: true
        },
        xAxis: {
            title: {
                text: 'Date'
            },
            categories: powerStatesData.dates // Ensure this is populated
        },
        yAxis: {
            allowDecimals: false,
            min: 0,
            tickInterval: 20, // Set the interval to 20
            title: {
                text: '% of Day'
            },
            labels: {
                format: '{value}.0', // Customize the label format
                style: {
                    color: '#000', // Change label color
                    fontSize: '12px' // Change label font size
                }
            },
            gridLineWidth: 1, // Adjust the width of the grid lines
            gridLineColor: '#e0e0e0'
        },
        tooltip: {
            useHTML: true,
            formatter: function () {
                return `<div style="margin: -8px;">
                            <div style="font-size: 13px; font-weight: 600; color: #616161; background-color: #eceff1; padding: 5px;">${
                                this.key
                            }</div>
                            <div style="display: flex; align-items: center; justify-content: center; padding-top: 10px; padding-bottom: 10px; margin-left: 10px; margin-right: 10px;">
                                <div style="width: 12px; height: 12px; border-radius: 50%; background-color: ${
                                    this.series.color || '#000'
                                }; margin-right: 10px;"></div>
                                <div style="font-size: 13px; color: #616161; display: flex;"><div style="margin-right: 7px;">${
                                    this.series.name
                                }:</div> <div style="font-weight: 600;">${this.y}</div></div>
                            </div>
                        </div>`;
            }
        },
        plotOptions: {
            column: {
                stacking: 'normal'
            }
        },
        series: [
            {
                name: 'Unknown',
                data: powerStatesData.Unknown.map((value) => parseFloat(value)) || [] // Default to empty array if undefined
            },
            {
                name: 'All Off',
                data: powerStatesData.All_Off.map((value) => parseFloat(value)) || [] // Default to empty array if undefined
            },
            {
                name: 'All Sleep',
                data: powerStatesData.All_Asleep.map((value) => parseFloat(value)) || [] // Default to empty array if undefined
            },
            {
                name: 'CPU On',
                data: powerStatesData.CPU_On.map((value) => parseFloat(value)) || [] // Default to empty array if undefined
            },
            {
                name: 'All On',
                data: powerStatesData.All_On.map((value) => parseFloat(value)) || [] // Default to empty array if undefined
            },
            {
                name: 'User Active',
                data: powerStatesData.User_Active.map((value) => parseFloat(value)) || [] // Default to empty array if undefined
            }
        ]
    };
    return (
        <MainCard title="Activity Reports Management" cardHeaderTitle="Power State and Activity Report" cardHeaderShow={true}>
            <Grid container spacing={gridSpacing}>
                <Grid item xs={10}>
                    <Typography sx={{ fontSize: '16px' }}>
                        Provides a stacked barchart showing the various interactions of CPU and display state with user activity
                    </Typography>
                </Grid>
                {powerStatesData.dates.length > 0 && (
                    <Grid item xs={2}>
                        <ReportFileExports
                            downloadFileName="power_state_report"
                            downloadFileOption="all"
                            headers={[]}
                            data={[
                                [''].concat(powerStatesData.dates),
                                ['User Active'].concat(powerStatesData.User_Active),
                                ['All On'].concat(powerStatesData.All_On),
                                ['CPU On'].concat(powerStatesData.CPU_On),
                                ['All Sleep'].concat(powerStatesData.All_Asleep),
                                ['All Off'].concat(powerStatesData.All_Off),
                                ['Unknown'].concat(powerStatesData.Unknown)
                            ]}
                            filters={filters}
                            setExportLoading={setExportLoading}
                        />
                    </Grid>
                )}
                <Grid item xs={3}>
                    <SubCard title="Filters">
                        <Grid container>
                            <Grid item xs={12}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Start Date"
                                        value={startDate}
                                        maxDate={endDate}
                                        onChange={(newValue) => {
                                            setStartDate(newValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                sx={{ width: '100%' }}
                                                helperText={
                                                    dateRangeValue === '0'
                                                        ? `For ${timeInterval}ly intervals, max range is ${getHelperText(timeInterval)}`
                                                        : ''
                                                }
                                            />
                                        )}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 1.7 }}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="End Date"
                                        value={endDate}
                                        maxDate={today}
                                        onChange={(newValue) => {
                                            setEndDate(newValue);
                                        }}
                                        renderInput={(params) => <TextField {...params} sx={{ width: '100%' }} />}
                                    />
                                </LocalizationProvider>
                            </Grid>

                            <Grid item xs={12} sx={{ mt: 1.7 }}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Date Range</InputLabel>
                                    <Select
                                        label="Date Range"
                                        value={dateRangeValue}
                                        name="date_range"
                                        onChange={handleChangeDateRangeValue}
                                    >
                                        {timeIntervalDateRanges[timeInterval] &&
                                            timeIntervalDateRanges[timeInterval].map((data, key) => (
                                                <MenuItem value={data.value} key={key}>
                                                    {data.label}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 1.7 }}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Time Interval</InputLabel>
                                    <Select
                                        label="Time Interval"
                                        value={timeInterval}
                                        name="time_interval"
                                        onChange={handleTimeIntervalChange}
                                    >
                                        <MenuItem value="hour">Hour</MenuItem>
                                        <MenuItem value="day">Day</MenuItem>
                                        <MenuItem value="week">Week</MenuItem>
                                        <MenuItem value="month">Month</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 1.7 }}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select Group</InputLabel>
                                    <Select
                                        label="Select Group"
                                        value={groupId}
                                        name="group"
                                        onChange={(e) => setGroupId(Number(e.target.value))}
                                    >
                                        <MenuItem value={0}>Any</MenuItem>
                                        {groupsData.length > 0 &&
                                            groupsData.map((data, key) => (
                                                <MenuItem value={data.id} key={key}>
                                                    {data.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 1.7 }}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select Policy</InputLabel>
                                    <Select
                                        label="Select Policy"
                                        value={policyId}
                                        name="policy"
                                        onChange={(e) => setPolicyId(Number(e.target.value))}
                                    >
                                        <MenuItem value={0}>Any</MenuItem>
                                        {policiesData.length > 0 &&
                                            policiesData.map((data, key) => (
                                                <MenuItem value={data.policyid} key={key}>
                                                    {data.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 1.7 }}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select View</InputLabel>
                                    <Select
                                        label="Select View"
                                        value={viewId}
                                        name="view"
                                        onChange={(e) => setViewId(Number(e.target.value))}
                                    >
                                        <MenuItem value={0}>Any</MenuItem>
                                        {viewData.length > 0 &&
                                            viewData.map((data, key) => (
                                                <MenuItem value={data.id + 1} key={key}>
                                                    {data.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 4 }}>
                                <LoadingButton
                                    color="primary"
                                    variant="contained"
                                    sx={{ width: '100%' }}
                                    loading={loading}
                                    onClick={getPowerStateData}
                                >
                                    Submit
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </SubCard>
                </Grid>
                <Grid
                    item
                    xs={9}
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                >
                    <ReportVisualization
                        loading={loading}
                        data={powerStatesData.dates}
                        type="highcharts"
                        highchartsOptions={options}
                        width="80%"
                    />
                </Grid>
            </Grid>
            {exportLoading === true ? (
                <div className="overlayStyle">
                    <div className="spinner spinnerStyle"></div>
                </div>
            ) : null}
        </MainCard>
    );
};

export default ReportsPowerState;
